import React, { useEffect, useRef, useState } from "react";

import { NavLink, useNavigate } from "react-router-dom";

import Select from "react-select";
import { toast } from "react-toastify";

import { createPartner, uploadPartnerPdf } from "../Services/partnerService";

import { RoutesPaths, countryCodes } from "../Router/constant";

export default function AddVendor() {
  const navigate = useNavigate();

  const countryRef = useRef(null);

  /* =========================================
     DEFAULT COUNTRY
  ========================================= */

  const getDefaultCountry = () =>
    countryCodes.find((country) => country.code === "AO") ||
    countryCodes.find((country) => country.dial_code === "+244") ||
    countryCodes[0];

  /* =========================================
     FORM STATE
  ========================================= */

  const [form, setForm] = useState({
    companyName: "",

    businessRegistrationNumber: "",

    serviceTypes: [],

    email: "",

    headquartersAddress: "",

    countryCode: "+244",

    countryIso: "AO",

    phone: "",

    contactPersonName: "",

    contactPersonTitle: "",

    website: "",

    numberOfEmployees: "",

    yearOfExperience: "",

    geographicCoverageAreas: [],

    warehousingCapacity: "",
  });

  /* =========================================
     FILES
  ========================================= */

  const [files, setFiles] = useState({
    commercialCertificate: null,

    operatingLicense: null,

    insuranceCertificate: null,
  });

  /* =========================================
     FLEET
  ========================================= */

  const [fleet, setFleet] = useState([
    {
      vehicleType: "",

      qty: "",

      capacity: "",
    },
  ]);

  /* =========================================
     SUBMIT LOADER
  ========================================= */

  const [saving, setSaving] = useState(false);

  /* =========================================
     COUNTRY
  ========================================= */

  const [isCountryOpen, setIsCountryOpen] = useState(false);

  const [countrySearch, setCountrySearch] = useState("");

  const [selectedCountry, setSelectedCountry] = useState(getDefaultCountry());

  /* =========================================
     ERRORS
  ========================================= */

  const [errors, setErrors] = useState({});

  /* =========================================
     SERVICE OPTIONS
  ========================================= */

  const serviceTypesOptions = [
    {
      value: "Air freight",
      label: "Air freight",
    },

    {
      value: "Sea freight",
      label: "Sea freight",
    },

    {
      value: "Rail freight",
      label: "Rail freight",
    },

    {
      value: "Trucking",
      label: "Trucking",
    },

    {
      value: "Warehouse",
      label: "Warehouse",
    },
  ];

  /* =========================================
     GEOGRAPHIC COVERAGE OPTIONS
  ========================================= */

  const geographicCoverageOptions = countryCodes
    .map((country) => {
      const name =
        country.name || country.country || country.label || country.code;

      return {
        value: name,
        label: name,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  /* =========================================
     REACT SELECT STYLE
  ========================================= */

  const customStyles = {
    control: (base) => ({
      ...base,

      border: "none",

      boxShadow: "none",

      background: "transparent",

      minHeight: "unset",
    }),

    multiValueLabel: (base) => ({
      ...base,

      fontSize: "11px",
    }),

    menuPortal: (base) => ({
      ...base,

      zIndex: 99999,
    }),
  };

  /* =========================================
     UPDATE FORM
  ========================================= */

  const update = (key, value) => {
    setForm((prev) => ({
      ...prev,

      [key]: value,
    }));

    setErrors((prev) => ({
      ...prev,

      [key]: "",
    }));
  };

  /* =========================================
     NORMAL INPUT CHANGE
  ========================================= */

  const handleChange = (event) => {
    const { name, value } = event.target;

    let finalValue = value;

    /* PHONE NUMERIC ONLY */

    if (name === "phone") {
      finalValue = value.replace(/\D/g, "");
    }

    /* TAX ID */

    if (name === "businessRegistrationNumber") {
      finalValue = value.replace(/[^a-zA-Z0-9]/g, "");
    }

    /* EMPLOYEE / EXPERIENCE */

    if (name === "numberOfEmployees" || name === "yearOfExperience") {
      finalValue = value.replace(/\D/g, "");
    }

    update(name, finalValue);
  };

  /* =========================================
     COUNTRY SEARCH
  ========================================= */

  const filteredCountries = countryCodes.filter((country) => {
    const search = String(countrySearch || "")
      .trim()
      .toLowerCase();

    if (!search) {
      return true;
    }

    const countryName = String(
      country.name || country.country || country.label || "",
    ).toLowerCase();

    const countryCode = String(country.code || "").toLowerCase();

    const dialCode = String(country.dial_code || "").toLowerCase();

    return (
      countryName.includes(search) ||
      countryCode.includes(search) ||
      dialCode.includes(search)
    );
  });

  /* =========================================
     COUNTRY SELECT
  ========================================= */

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);

    setForm((prev) => ({
      ...prev,

      countryCode: country.dial_code,

      countryIso: country.code,
    }));

    setIsCountryOpen(false);

    setCountrySearch("");
  };

  /* =========================================
     COUNTRY OUTSIDE CLICK
  ========================================= */

  useEffect(() => {
    const handleOutside = (event) => {
      if (countryRef.current && !countryRef.current.contains(event.target)) {
        setIsCountryOpen(false);

        setCountrySearch("");
      }
    };

    document.addEventListener("mousedown", handleOutside);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
    };
  }, []);

  /* =========================================
     FLEET ADD
  ========================================= */

  const addCapacity = () => {
    setFleet((prev) => [
      ...prev,

      {
        vehicleType: "",

        qty: "",

        capacity: "",
      },
    ]);
  };

  /* =========================================
     FLEET REMOVE
  ========================================= */

  const removeCapacity = (index) => {
    if (fleet.length === 1) {
      return;
    }

    setFleet((prev) => prev.filter((_, i) => i !== index));
  };

  /* =========================================
     FLEET CHANGE
  ========================================= */

  const handleCapacityChange = (index, field, value) => {
    let finalValue = value;

    if (field === "qty" || field === "capacity") {
      finalValue = value.replace(/\D/g, "");
    }

    setFleet((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,

              [field]: finalValue,
            }
          : item,
      ),
    );
  };

  /* =========================================
     FILE CHANGE
  ========================================= */

  const handleFileChange = (event, field) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    /* PDF ONLY */

    if (file.type !== "application/pdf") {
      toast.error("Please upload PDF file only.");

      event.target.value = "";

      return;
    }

    setFiles((prev) => ({
      ...prev,

      [field]: file,
    }));

    /* CLEAR DOCUMENT ERROR */

    setErrors((prev) => ({
      ...prev,

      [field]: "",
    }));
  };

  /* =========================================
     REMOVE FILE
  ========================================= */

  const removeFile = (field) => {
    setFiles((prev) => ({
      ...prev,

      [field]: null,
    }));

    /*
      Do not show any required message
      underneath document upload.
    */

    setErrors((prev) => ({
      ...prev,

      [field]: "",
    }));
  };

  /* =========================================
     UPLOAD CARD
  ========================================= */

  const UploadCard = ({ title, field, required = false }) => {
    const inputId = `vendor-${field}`;

    const file = files[field];

    return (
      <div
        id={`field-${field}`}
        className={`upload-card ${errors[field] ? "upload-card-error" : ""}`}
        onClick={() => document.getElementById(inputId)?.click()}
        style={{
          cursor: "pointer",
        }}
      >
        {/* ICON */}

        <div className="upload-card-left">
          <img src="/assets/img/progressIconContainer.svg" alt="" />
        </div>

        {/* CONTENT */}

        <div className="upload-card-content">
          <div className="upload-card-title">
            {title}

            {required && <span className="upload-card-required ms-1">*</span>}
          </div>

          <div className="upload-card-meta">
            <span>File format: PDF</span>

            <span>File size: 5MB</span>
          </div>

          {file && <small className="upload-card-file-name">{file.name}</small>}
        </div>

        {/* HIDDEN INPUT */}

        <input
          id={inputId}
          type="file"
          accept="application/pdf"
          style={{
            display: "none",
          }}
          onChange={(event) => handleFileChange(event, field)}
        />

        {/* REMOVE FILE */}

        {file && (
          <button
            type="button"
            className="upload-card-closeBtn"
            onClick={(event) => {
              event.stopPropagation();

              removeFile(field);
            }}
          >
            ✕
          </button>
        )}

        {/* PROGRESS */}

        <div className="upload-card-progressWrapper">
          <div
            className={`upload-card-progressBar ${
              file ? "upload-card-progressBar--active" : ""
            }`}
            style={{
              width: file ? "100%" : "0%",
            }}
          />
        </div>
      </div>
    );
  };

  /* =========================================
     SCROLL TO FIRST REQUIRED ERROR
  ========================================= */

  const scrollToFirstError = (newErrors) => {
    /*
      This array also controls validation
      scroll priority.
    */

    const fieldOrder = [
      "companyName",

      "businessRegistrationNumber",

      "serviceTypes",

      "email",

      "headquartersAddress",

      "phone",

      "contactPersonName",

      "contactPersonTitle",

      "commercialCertificate",

      "operatingLicense",

      "insuranceCertificate",
    ];

    const firstErrorField = fieldOrder.find((field) => newErrors[field]);

    if (!firstErrorField) {
      return;
    }

    /*
      Wait for React error state
      to render first.
    */

    setTimeout(() => {
      const element = document.getElementById(`field-${firstErrorField}`);

      if (!element) {
        return;
      }

      element.scrollIntoView({
        behavior: "smooth",

        block: "center",
      });

      /*
        Focus only visible normal inputs.

        We do not focus hidden PDF input.
      */

      const input = element.querySelector('input:not([type="file"]), textarea');

      if (input && typeof input.focus === "function") {
        setTimeout(() => {
          input.focus({
            preventScroll: true,
          });
        }, 500);
      }
    }, 100);
  };

  /* =========================================
     VALIDATION
  ========================================= */

  const validateForm = () => {
    const newErrors = {};

    /* COMPANY NAME */

    if (!form.companyName.trim()) {
      newErrors.companyName = "Please enter company name.";
    }

    /* BUSINESS REGISTRATION */

    if (!form.businessRegistrationNumber.trim()) {
      newErrors.businessRegistrationNumber =
        "Please enter business registration number.";
    }

    /* SERVICE TYPES */

    if (!form.serviceTypes.length) {
      newErrors.serviceTypes = "Please select service type.";
    }

    /* EMAIL */

    if (!form.email.trim()) {
      newErrors.email = "Please enter email address.";
    }

    /* HEADQUARTERS */

    if (!form.headquartersAddress.trim()) {
      newErrors.headquartersAddress = "Please enter headquarters address.";
    }

    /* PHONE */

    if (!form.phone.trim()) {
      newErrors.phone = "Please enter phone number.";
    }

    /* CONTACT NAME */

    if (!form.contactPersonName.trim()) {
      newErrors.contactPersonName = "Please enter contact person name.";
    }

    /* CONTACT TITLE */

    if (!form.contactPersonTitle.trim()) {
      newErrors.contactPersonTitle = "Please enter contact person title.";
    }

    /* =========================================
       REQUIRED DOCUMENTS

       true only.
       No document error message shown.
    ========================================= */

    if (!files.commercialCertificate) {
      newErrors.commercialCertificate = true;
    }

    if (!files.operatingLicense) {
      newErrors.operatingLicense = true;
    }

    if (!files.insuranceCertificate) {
      newErrors.insuranceCertificate = true;
    }

    setErrors(newErrors);

    const isValid = Object.keys(newErrors).length === 0;

    if (!isValid) {
      scrollToFirstError(newErrors);
    }

    return isValid;
  };

  /* =========================================
     SUBMIT
  ========================================= */

  const submit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    /* =====================================
       FLEET FORMAT
    ===================================== */

    const formattedFleet = fleet
      .filter((item) => item.vehicleType.trim() || item.qty || item.capacity)
      .map((item) => ({
        vehicleType: item.vehicleType.trim(),

        qty: item.qty,

        capacity: item.capacity,
      }));

    /* =====================================
       PAYLOAD
    ===================================== */

    const payload = {
      companyName: form.companyName,

      businessRegistrationNumber: form.businessRegistrationNumber,

      /*
       * EXACT SERVICE FORMAT:
       *
       * [
       *   "Air freight",
       *   "Sea freight"
       * ]
       */

      serviceTypes: form.serviceTypes,

      email: form.email,

      headquartersAddress: {
        address: form.headquartersAddress,

        longitude: 0,

        latitude: 0,
      },

      countryCode: selectedCountry?.dial_code || "+244",

      phone: form.phone,

      contactPersonName: form.contactPersonName,

      contactPersonTitle: form.contactPersonTitle,

      website: form.website,

      numberOfEmployees: form.numberOfEmployees,

      yearOfExperience: form.yearOfExperience,

      geographicCoverageAreas: form.geographicCoverageAreas.join(", "),

      fleetCapacity: formattedFleet,

      warehousingCapacity: form.warehousingCapacity,

      commercialCertificate: "",

      operatingLicense: "",

      insuranceCertificate: "",
    };

    try {
      setSaving(true);

      /* =====================================
         UPLOAD DOCUMENTS FIRST
      ===================================== */

      for (const [key, file] of Object.entries(files)) {
        if (file) {
          const upload = await uploadPartnerPdf(file);

          payload[key] =
            upload?.data?.data?.id ||
            upload?.data?.data?._id ||
            upload?.data?.data?.fileId ||
            upload?.data?.data?.filename ||
            upload?.data?.filename;

          if (!payload[key]) {
            throw new Error(`Document upload failed: ${file.name}`);
          }
        }
      }

      /* =====================================
         CHECK FINAL PAYLOAD
      ===================================== */

      console.log("Create vendor payload:", payload);

      console.log("Service types payload:", payload.serviceTypes);

      /* =====================================
         CREATE VENDOR
      ===================================== */

      const response = await createPartner(payload);

      const message =
        response?.data?.data?.message ||
        response?.data?.message ||
        "Vendor created successfully.";

      if (response?.data?.status === false) {
        throw new Error(message);
      }

      toast.success(message);

      navigate(RoutesPaths.VENDOR);
    } catch (error) {
      console.error("Create vendor:", error);

      toast.error(
        error?.response?.data?.data?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to create vendor.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid add-vendor-page">
      {/* =====================================
          BREADCRUMB
      ===================================== */}

      <div className="logisticsMenu">
        <NavLink to={RoutesPaths.VENDOR}>
          <img src="/assets/img/homeIcon.svg" alt="" />
        </NavLink>

        <img src="/assets/img/chevron-right.svg" alt="" />

        <span>Add vendor</span>
      </div>

      <form onSubmit={submit}>
        <div className="row productAttributesSection">
          {/* =====================================
              COMPANY INFORMATION
          ===================================== */}

          <div className="col-md-12 productAttributesbg">
            <div className="row">
              <div className="col-md-12 productAttributesTitle">
                <div>
                  <h6 className="m-0">Company information</h6>

                  <p className="m-0">
                    Provide the vendor&apos;s company and contact details.
                  </p>
                </div>
              </div>
            </div>

            <div className="row">
              {/* =================================
                  COMPANY NAME
              ================================= */}

              <div className="col-md-6 p-0">
                <div id="field-companyName" className="productInputSection">
                  <div className="customInputAction">
                    <label>
                      Company name <span>*</span>
                    </label>

                    <input
                      type="text"
                      name="companyName"
                      className={`customInputText ${
                        errors.companyName ? "customInputText-error" : ""
                      }`}
                      placeholder="Enter full legal business name"
                      value={form.companyName}
                      onChange={handleChange}
                    />
                  </div>

                  {errors.companyName && (
                    <p className="m-0 errorText">{errors.companyName}</p>
                  )}
                </div>
              </div>

              {/* =================================
                  BUSINESS REGISTRATION
              ================================= */}

              <div className="col-md-6 p-0">
                <div
                  id="field-businessRegistrationNumber"
                  className="productInputSection"
                >
                  <div className="customInputAction">
                    <label>
                      Business registration number / Tax ID <span>*</span>
                    </label>

                    <input
                      type="text"
                      name="businessRegistrationNumber"
                      className={`customInputText ${
                        errors.businessRegistrationNumber
                          ? "customInputText-error"
                          : ""
                      }`}
                      placeholder="Enter business registration number"
                      value={form.businessRegistrationNumber}
                      onChange={handleChange}
                    />
                  </div>

                  {errors.businessRegistrationNumber && (
                    <p className="m-0 errorText">
                      {errors.businessRegistrationNumber}
                    </p>
                  )}
                </div>
              </div>

              {/* =================================
                  SERVICE TYPES
              ================================= */}

              <div className="col-md-6 p-0">
                <div id="field-serviceTypes" className="productInputSection">
                  <div className="customInputActionSelect">
                    <label>
                      Service types <span>*</span>
                    </label>

                    <Select
                      isMulti
                      options={serviceTypesOptions}
                      placeholder="Select service type"
                      styles={customStyles}
                      className={`customInputText ${
                        errors.serviceTypes ? "customInputText-error" : ""
                      }`}
                      closeMenuOnSelect={false}
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                      value={serviceTypesOptions.filter((option) =>
                        form.serviceTypes.includes(option.value),
                      )}
                      onChange={(selected) => {
                        const values = selected
                          ? selected.map((option) => option.value)
                          : [];

                        update("serviceTypes", values);
                      }}
                    />
                  </div>

                  {errors.serviceTypes && (
                    <p className="m-0 errorText">{errors.serviceTypes}</p>
                  )}
                </div>
              </div>

              {/* =================================
                  EMAIL
              ================================= */}

              <div className="col-md-6 p-0">
                <div id="field-email" className="productInputSection">
                  <div className="customInputAction">
                    <label>
                      Email address <span>*</span>
                    </label>

                    <input
                      type="email"
                      name="email"
                      className={`customInputText ${
                        errors.email ? "customInputText-error" : ""
                      }`}
                      placeholder="Enter email address"
                      value={form.email}
                      onChange={handleChange}
                    />
                  </div>

                  {errors.email && (
                    <p className="m-0 errorText">{errors.email}</p>
                  )}
                </div>
              </div>

              {/* =================================
                  HEADQUARTERS
              ================================= */}

              <div className="col-md-6 p-0">
                <div
                  id="field-headquartersAddress"
                  className="productInputSection"
                >
                  <div className="customInputAction">
                    <label>
                      Headquarters address <span>*</span>
                    </label>

                    <input
                      type="text"
                      name="headquartersAddress"
                      className={`customInputText ${
                        errors.headquartersAddress
                          ? "customInputText-error"
                          : ""
                      }`}
                      placeholder="Enter headquarters address"
                      value={form.headquartersAddress}
                      onChange={handleChange}
                    />
                  </div>

                  {errors.headquartersAddress && (
                    <p className="m-0 errorText">
                      {errors.headquartersAddress}
                    </p>
                  )}
                </div>
              </div>

              {/* =================================
                  PHONE
              ================================= */}

              <div className="col-md-6 p-0">
                <div id="field-phone" className="productInputSection">
                  <div className="customInputAction">
                    <label>
                      Phone number <span>*</span>
                    </label>

                    <div className="phoneInputWrapper">
                      {/* COUNTRY */}

                      <div
                        className={`countryDropdown ${
                          isCountryOpen ? "countryDropdown-open" : ""
                        }`}
                        ref={countryRef}
                      >
                        <div
                          className="countryDropdown-trigger"
                          onClick={() => {
                            setIsCountryOpen((prev) => !prev);

                            if (isCountryOpen) {
                              setCountrySearch("");
                            }
                          }}
                        >
                          <div className="countryDropdown-selected">
                            <img
                              src={`https://flagcdn.com/24x18/${selectedCountry.code.toLowerCase()}.png`}
                              alt={selectedCountry.code}
                            />

                            <span className="countryDropdown-text">
                              {selectedCountry.dial_code} (
                              {selectedCountry.code})
                            </span>
                          </div>

                          <div className="countryDropdown-arrow">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              style={{
                                transform: isCountryOpen
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",

                                transition: "transform 0.2s ease",
                              }}
                            >
                              <path
                                d="M7 10l5 5 5-5"
                                stroke="#555"
                                strokeWidth="1.6"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>

                        {/* COUNTRY MENU */}

                        {isCountryOpen && (
                          <div
                            className="countryDropdown-menu"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <div className="countryDropdown-search">
                              <div className="countryDropdown-searchInputWrapper">
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <circle
                                    cx="11"
                                    cy="11"
                                    r="7"
                                    stroke="currentColor"
                                    strokeWidth="1.7"
                                  />

                                  <path
                                    d="M16.2 16.2L21 21"
                                    stroke="currentColor"
                                    strokeWidth="1.7"
                                    strokeLinecap="round"
                                  />
                                </svg>

                                <input
                                  type="text"
                                  value={countrySearch}
                                  autoFocus
                                  placeholder="Search country or code"
                                  className="countryDropdown-searchInput"
                                  onClick={(event) => event.stopPropagation()}
                                  onChange={(event) =>
                                    setCountrySearch(event.target.value)
                                  }
                                />

                                {countrySearch && (
                                  <button
                                    type="button"
                                    className="countryDropdown-clearSearch"
                                    onClick={(event) => {
                                      event.stopPropagation();

                                      setCountrySearch("");
                                    }}
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            </div>

                            <div className="countryDropdown-list">
                              {filteredCountries.length > 0 ? (
                                filteredCountries.map((country) => {
                                  const isSelected =
                                    selectedCountry?.code === country.code;

                                  return (
                                    <div
                                      key={`${country.code}-${country.dial_code}`}
                                      className={`countryDropdown-item ${
                                        isSelected
                                          ? "countryDropdown-itemSelected"
                                          : ""
                                      }`}
                                      onClick={(event) => {
                                        event.stopPropagation();

                                        handleCountrySelect(country);
                                      }}
                                    >
                                      <div className="countryDropdown-itemCountry">
                                        <img
                                          src={`https://flagcdn.com/24x18/${country.code.toLowerCase()}.png`}
                                          alt={country.code}
                                        />

                                        <div className="countryDropdown-itemDetails">
                                          <span className="countryDropdown-countryName">
                                            {country.name ||
                                              country.country ||
                                              country.label ||
                                              country.code}
                                          </span>

                                          <span className="countryDropdown-itemCode">
                                            {country.dial_code} ({country.code})
                                          </span>
                                        </div>
                                      </div>

                                      {isSelected && (
                                        <span className="countryDropdown-check">
                                          ✓
                                        </span>
                                      )}
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="countryDropdown-noResult">
                                  No country found
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* PHONE */}

                      <input
                        type="text"
                        name="phone"
                        className={`customInputText phoneNumberInput ${
                          errors.phone ? "customInputText-error" : ""
                        }`}
                        placeholder="Enter phone number"
                        value={form.phone}
                        onChange={handleChange}
                        inputMode="numeric"
                      />
                    </div>
                  </div>

                  {errors.phone && (
                    <p className="m-0 errorText">{errors.phone}</p>
                  )}
                </div>
              </div>

              {/* =================================
                  CONTACT PERSON NAME
              ================================= */}

              <div className="col-md-6 p-0">
                <div
                  id="field-contactPersonName"
                  className="productInputSection"
                >
                  <div className="customInputAction">
                    <label>
                      Contact person name <span>*</span>
                    </label>

                    <input
                      type="text"
                      name="contactPersonName"
                      className={`customInputText ${
                        errors.contactPersonName ? "customInputText-error" : ""
                      }`}
                      placeholder="Enter contact person name"
                      value={form.contactPersonName}
                      onChange={handleChange}
                    />
                  </div>

                  {errors.contactPersonName && (
                    <p className="m-0 errorText">{errors.contactPersonName}</p>
                  )}
                </div>
              </div>

              {/* =================================
                  CONTACT PERSON TITLE
              ================================= */}

              <div className="col-md-6 p-0">
                <div
                  id="field-contactPersonTitle"
                  className="productInputSection"
                >
                  <div className="customInputAction">
                    <label>
                      Contact person title <span>*</span>
                    </label>

                    <input
                      type="text"
                      name="contactPersonTitle"
                      className={`customInputText ${
                        errors.contactPersonTitle ? "customInputText-error" : ""
                      }`}
                      placeholder="Enter contact person title"
                      value={form.contactPersonTitle}
                      onChange={handleChange}
                    />
                  </div>

                  {errors.contactPersonTitle && (
                    <p className="m-0 errorText">{errors.contactPersonTitle}</p>
                  )}
                </div>
              </div>

              {/* =================================
                  WEBSITE
              ================================= */}

              <div className="col-md-6 p-0">
                <div className="productInputSection">
                  <div className="customInputAction">
                    <label>Website</label>

                    <input
                      type="text"
                      name="website"
                      className="customInputText"
                      placeholder="Enter website"
                      value={form.website}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              {/* =================================
                  EMPLOYEES
              ================================= */}

              <div className="col-md-6 p-0">
                <div className="productInputSection">
                  <div className="customInputAction">
                    <label>Number of employees</label>

                    <input
                      type="text"
                      name="numberOfEmployees"
                      className="customInputText"
                      placeholder="Enter number of employees"
                      value={form.numberOfEmployees}
                      onChange={handleChange}
                      inputMode="numeric"
                    />
                  </div>
                </div>
              </div>

              {/* =================================
                  EXPERIENCE
              ================================= */}

              <div className="col-md-6 p-0">
                <div className="productInputSection">
                  <div className="customInputAction">
                    <label>Years of experience</label>

                    <input
                      type="text"
                      name="yearOfExperience"
                      className="customInputText"
                      placeholder="Enter years of experience"
                      value={form.yearOfExperience}
                      onChange={handleChange}
                      inputMode="numeric"
                    />
                  </div>
                </div>
              </div>

              {/* =================================
                  GEOGRAPHIC COVERAGE
              ================================= */}

              <div className="col-md-6 p-0">
                <div className="productInputSection">
                  <div className="customInputActionSelect">
                    <label>Geographic coverage areas</label>

                    <Select
                      isMulti
                      placeholder="Select geographic coverage areas"
                      options={geographicCoverageOptions}
                      styles={customStyles}
                      className="customInputText"
                      closeMenuOnSelect={false}
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                      value={geographicCoverageOptions.filter((option) =>
                        form.geographicCoverageAreas.includes(option.value),
                      )}
                      onChange={(selected) => {
                        const values = selected
                          ? selected.map((option) => option.value)
                          : [];

                        update("geographicCoverageAreas", values);
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* =================================
                  FLEET
              ================================= */}

              <div className="col-md-6 p-0">
                <div className="productInputSection">
                  <div className="customInputAction">
                    <div className="feetCapacityAdd">
                      <label>Fleet capacity</label>

                      <button
                        type="button"
                        className="addCapacity"
                        onClick={addCapacity}
                      >
                        Add
                      </button>
                    </div>

                    {fleet.map((item, index) => (
                      <div className="enterVehicleCapacity" key={index}>
                        <input
                          type="text"
                          className="customInputText enterCapacity"
                          placeholder="Enter vehicle type"
                          value={item.vehicleType}
                          onChange={(event) =>
                            handleCapacityChange(
                              index,
                              "vehicleType",
                              event.target.value,
                            )
                          }
                        />

                        <input
                          type="text"
                          inputMode="numeric"
                          className="customInputText enterCapacity"
                          placeholder="Enter qty"
                          value={item.qty}
                          onChange={(event) =>
                            handleCapacityChange(
                              index,
                              "qty",
                              event.target.value,
                            )
                          }
                        />

                        <input
                          type="text"
                          inputMode="numeric"
                          className="customInputText enterCapacity"
                          placeholder="Enter capacity"
                          value={item.capacity}
                          onChange={(event) =>
                            handleCapacityChange(
                              index,
                              "capacity",
                              event.target.value,
                            )
                          }
                        />

                        {fleet.length > 1 && (
                          <button
                            type="button"
                            className="closeCapacityField"
                            onClick={() => removeCapacity(index)}
                          >
                            <img
                              src="/assets/img/x-imageClose.svg"
                              alt="close"
                            />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* =================================
                  WAREHOUSE
              ================================= */}

              <div className="col-md-6 p-0">
                <div className="productInputSection">
                  <div className="customInputAction">
                    <label>Warehousing capacity</label>

                    <input
                      type="text"
                      name="warehousingCapacity"
                      className="customInputText"
                      placeholder="Enter warehousing capacity"
                      value={form.warehousingCapacity}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* =====================================
              LICENSING AND COMPLIANCE
          ===================================== */}

          <div className="col-md-12 productAttributesbg">
            <div className="row">
              <div className="col-md-12 productAttributesTitle">
                <div>
                  <h6 className="m-0">Licensing and compliance</h6>

                  <p className="m-0">
                    Upload vendor documents for verification.
                  </p>
                </div>
              </div>
            </div>

            <div className="row">
              {/* COMMERCIAL CERTIFICATE */}

              <div className="productInputSection col-md-6">
                <UploadCard
                  title="Commercial certificate"
                  field="commercialCertificate"
                  required
                />
              </div>

              {/* OPERATING LICENSE */}

              <div className="productInputSection col-md-6">
                <UploadCard
                  title="Operating license"
                  field="operatingLicense"
                  required
                />
              </div>

              {/* INSURANCE CERTIFICATE */}

              <div className="productInputSection col-md-6">
                <UploadCard
                  title="Insurance certificate"
                  field="insuranceCertificate"
                  required
                />
              </div>
            </div>
          </div>
        </div>

        {/* =====================================
            ACTIONS
        ===================================== */}

        <div className="add-vendor-actions">
          <NavLink to={RoutesPaths.VENDOR}>Cancel</NavLink>

          <button
            type="submit"
            className="createOrderBtnDetail"
            disabled={saving}
          >
            {saving ? "Saving..." : "Create vendor"}
          </button>
        </div>
      </form>
    </div>
  );
}
